import {diff, rebase} from '@mintdata-wsp/thirdparty/jiff/jiff';
import {patch} from '@mintdata-wsp/thirdparty/jiff/lib/jsonPatch';
import {Category, Logger} from '@mintdata/mint-kit/api/log/mk';
import {Assert} from '@mintdata/mint-kit/impl/util/Assert';

const logger = Logger.getLogger(Category.Multiplayer);

const UNDO_REDO_ITEMS_LIMIT = 100;

export class UndoRedoStackItem
{
    public constructor(public prevBaseDesign?:any,
                       public nextBaseDesign?:any)
    {
    }
}

export class UndoRedoStack
{
    public items:Array<UndoRedoStackItem>;

    public currentItemIndex:number;

    public migrationAllowed = true;

    public constructor(initialDesign:any)
    {
        this.reset(initialDesign);
    }

    public reset(activeDesign:any)
    {
        this.items = [new UndoRedoStackItem(null, activeDesign)];
        this.currentItemIndex = 0;
    }

    public push(activeDesign:any, newDesign:any)
    {
        if( activeDesign.designerVersion != newDesign.designerVersion )
        {
            Assert.yes(this.migrationAllowed, 'migration should have not been happened on that point yet');
            Assert.yes(this.items.length == 1, 'undo stack should be empty on that point');

            // this is the first change caused by migration => just re-write inital design in stack
            logger.debug('UNDO-STACK :: Re-writing 1st undo stack item due to migration :: newDesign: ', newDesign);
            this.items[0].nextBaseDesign = newDesign;
            this.migrationAllowed = false;

            return;
        }

        const designDiff = diff(activeDesign, newDesign, {invertible: false});

        if( designDiff.length == 0 )
            return;

        logger.debug('Pushing new design into the undo stack :: activeDesign: ', activeDesign);
        logger.debug('Pushing new design into the undo stack :: newDesign: ', newDesign);
        logger.debug('Pushing new design into the undo stack :: diff: ', designDiff);

        if( this.currentItemIndex + 1 < this.items.length )
            this.items = this.items.slice(0, this.currentItemIndex + 1);

        this.items[this.currentItemIndex].nextBaseDesign = activeDesign;
        this.items.push(new UndoRedoStackItem(newDesign, null));
        this.currentItemIndex++;

        if( this.items.length > UNDO_REDO_ITEMS_LIMIT )
        {
            this.items.shift();
            this.currentItemIndex--;
        }

        this.migrationAllowed = false;
    }

    public undo(activeDesign:any):any
    {
        const {items, currentItemIndex} = this;

        if( !this.isUndoAllowed() )
            return activeDesign;

        logger.debugGroup('UNDO');

        const prevItem = items[currentItemIndex - 1];
        const currItem = items[currentItemIndex];

        // reverse diff from `currItem` to `prevItem`
        const currToPrevDiff = diff(currItem.prevBaseDesign, prevItem.nextBaseDesign, {invertible: true});

        // current changes (that came from server)
        const currToActiveDiff = diff(currItem.prevBaseDesign, activeDesign, {invertible: false});
        let newActiveDesign;

        try
        {
            const rebasedCurrToActiveDiff = rebase([currToPrevDiff], currToActiveDiff);

            logger.debug('UNDO :: activeDesign: ', activeDesign);
            logger.debug('UNDO :: currItem: ', currItem);
            logger.debug('UNDO :: prevItem: ', prevItem);
            logger.debug('UNDO :: currToPrevDiff: ', currToPrevDiff);
            logger.debug('UNDO :: currToActiveDiff: ', currToActiveDiff);
            logger.debug('UNDO :: rebasedCurrToActiveDiff: ', rebasedCurrToActiveDiff);

            newActiveDesign = patch(rebasedCurrToActiveDiff, prevItem.nextBaseDesign);
            logger.debug('UNDO :: newActiveDesign: ', newActiveDesign);
        }
        catch( error )
        {
            throw new Error('rebase error');
        }
        finally
        {
            logger.debugGroupEnd();
        }

        currItem.prevBaseDesign = activeDesign;
        prevItem.nextBaseDesign = newActiveDesign;
        this.currentItemIndex--;

        return newActiveDesign;
    }

    public redo(activeDesign:any):any
    {
        const {items, currentItemIndex} = this;

        if( !this.isRedoAllowed() )
            return activeDesign;

        logger.debugGroup('REDO');

        const currItem = items[currentItemIndex];
        const nextItem = items[currentItemIndex + 1];

        // diff from `currItem` to `nextItem`
        const currToNextDiff = diff(currItem.nextBaseDesign, nextItem.prevBaseDesign, {invertible: true});

        // current changes (that came from server)
        const currToActiveDiff = diff(currItem.nextBaseDesign, activeDesign, {invertible: false});
        let newActiveDesign;

        try
        {
            const rebasedCurrToActiveDiff = rebase([currToNextDiff], currToActiveDiff);

            logger.debug('REDO :: activeDesign: ', activeDesign);
            logger.debug('REDO :: currItem: ', currItem);
            logger.debug('REDO :: nextItem: ', nextItem);
            logger.debug('REDO :: currToNextDiff: ', currToNextDiff);
            logger.debug('REDO :: currToActiveDiff: ', currToActiveDiff);
            logger.debug('REDO :: rebasedCurrToActiveDiff: ', rebasedCurrToActiveDiff);

            newActiveDesign = patch(rebasedCurrToActiveDiff, nextItem.prevBaseDesign);
            logger.debug('REDO :: newActiveDesign: ', newActiveDesign);
        }
        catch( error )
        {
            throw new Error('rebase error');
        }
        finally
        {
            logger.debugGroupEnd();
        }

        currItem.nextBaseDesign = activeDesign;
        nextItem.prevBaseDesign = newActiveDesign;
        this.currentItemIndex++;

        return newActiveDesign;
    }

    public isUndoAllowed():boolean
    {
        return this.currentItemIndex > 0;
    }

    public isRedoAllowed():boolean
    {
        return this.currentItemIndex < this.items.length - 1;
    }
}
